import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AuthenticatedImage from './AuthenticatedImage';
import dummyImage from '../img/logo.png';
import './TournamentallDetails.css';

function TournamentDetails() {
  const location = useLocation();
  const navigate = useNavigate();
  const { tournament } = location.state || {};
  
  const [tournamentDetails, setTournamentDetails] = useState(null);
  const [teams, setTeams] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Function to construct proper image URL
  const constructImageUrl = (imagePath, type = 'general') => {
    if (!imagePath) return null;
    
    if (imagePath.startsWith('http')) {
      return imagePath;
    }
    
    const baseUrl = "https://api.asterickx.com/";
    let cleanPath = imagePath.replace(/^\/+/, '').replace(/^Assets\//, '');
    
    // Different endpoints for different image types
    switch(type) {
      case 'tournament':
        return baseUrl + 'TournamentLogos/' + cleanPath;
      case 'sponsor':
        return baseUrl + '' + cleanPath;
      case 'team':
        return baseUrl + '' + cleanPath;
      default:
        return baseUrl + cleanPath;
    }
  };

  // Fetch tournament details, teams, and sponsors
  useEffect(() => {
    const fetchTournamentData = async () => {
      if (!tournament) {
        setError('Tournament data not found');
        setLoading(false);
        return;
      }

      const token = localStorage.getItem('token');
      
      try {
        setLoading(true);
        
        // Fetch detailed tournament info
        const tournamentResponse = await fetch(
          `https://api.asterickx.com/api/Tournament/${tournament.id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (tournamentResponse.ok) {
          const tournamentData = await tournamentResponse.json();
          setTournamentDetails(tournamentData);
        }

        // Fetch teams for this tournament
        const teamsResponse = await fetch(
          `https://api.asterickx.com/api/Team/Get-tournament-by-ID/${tournament.id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (teamsResponse.ok) {
          const teamsData = await teamsResponse.json();
          setTeams(teamsData || []);
        }

        // Fetch sponsors for this tournament
        const sponsorsResponse = await fetch(
          `https://api.asterickx.com/api/Sponsor/get-tournament-by-ID/${tournament.id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (sponsorsResponse.ok) {
          const sponsorsData = await sponsorsResponse.json();
          setSponsors(sponsorsData || []);
        }

      } catch (error) {
        console.error('Error fetching tournament data:', error);
        setError('Failed to load tournament details');
      } finally {
        setLoading(false);
      }
    };

    fetchTournamentData();
  }, [tournament]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  if (loading) {
    return (
      <div className="tournament-details-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading tournament details...</p>
        </div>
      </div>
    );
  }

  if (error || !tournament) {
    return (
      <div className="tournament-details-container">
        <div className="error-message">
          <h3>Error</h3>
          <p>{error || 'Tournament not found'}</p>
          <button onClick={() => navigate('/')} className="btn-primary">
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  const currentTournament = tournamentDetails || tournament;

  return (
    <div className="tournament-details-container">
      {/* Hero Section */}
      <div className="tournament-hero-section">
        <div className="hero-background"></div>
        <div className="hero-content">
          <button onClick={() => navigate('/')} className="back-button">
            <span className="back-icon">←</span>
            Back to Home
          </button>
          
          <div className="tournament-main-info">
            <div className="tournament-logo-wrapper">
              <AuthenticatedImage
                src={constructImageUrl(currentTournament.tournamentLogo || currentTournament.logo, 'tournament')}
                alt={currentTournament.tournamentName || currentTournament.name}
                className="tournament-hero-logo"
                fallbackSrc={dummyImage}
              />
            </div>
            
            <div className="tournament-title-section">
              <h1 className="tournament-title">{currentTournament.tournamentName || currentTournament.name}</h1>
              <div className="tournament-subtitle">
                <span className="venue-info">📍 {currentTournament.venue}</span>
                <span className={`status-indicator ${currentTournament.status?.toLowerCase()}`}>
                  {currentTournament.status}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="tournament-content">
        {/* Stats Overview */}
        <div className="stats-overview">
          <div className="stat-card">
            <div className="stat-icon">🏆</div>
            <div className="stat-info">
              <span className="stat-number">{teams.length}</span>
              <span className="stat-label">Teams</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">🤝</div>
            <div className="stat-info">
              <span className="stat-number">{sponsors.length}</span>
              <span className="stat-label">Sponsors</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">📅</div>
            <div className="stat-info">
              <span className="stat-number">{formatDate(currentTournament.startDate)}</span>
              <span className="stat-label">Start Date</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">🏁</div>
            <div className="stat-info">
              <span className="stat-number">{formatDate(currentTournament.endDate)}</span>
              <span className="stat-label">End Date</span>
            </div>
          </div>
        </div>

        {/* Tournament Information */}
        <div className="tournament-info-section">
          <h2 className="section-title">Tournament Information</h2>
          <div className="info-grid">
            <div className="info-card">
              <div className="info-header">
                <h3>📋 Basic Details</h3>
              </div>
              <div className="info-content">
                <div className="info-row">
                  <span className="info-label">Tournament ID</span>
                  <span className="info-value">{currentTournament.tournamentId || currentTournament.id}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Name</span>
                  <span className="info-value">{currentTournament.tournamentName || currentTournament.name}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Venue</span>
                  <span className="info-value">{currentTournament.venue}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Status</span>
                  <span className={`info-value status-badge ${currentTournament.status?.toLowerCase()}`}>
                    {currentTournament.status}
                  </span>
                </div>
              </div>
            </div>

            <div className="info-card">
              <div className="info-header">
                <h3>⏰ Schedule</h3>
              </div>
              <div className="info-content">
                <div className="info-row">
                  <span className="info-label">Start Date & Time</span>
                  <span className="info-value">{formatDateTime(currentTournament.startDate)}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">End Date & Time</span>
                  <span className="info-value">{formatDateTime(currentTournament.endDate)}</span>
                </div>
                {currentTournament.registrationDeadline && (
                  <div className="info-row">
                    <span className="info-label">Registration Deadline</span>
                    <span className="info-value">{formatDateTime(currentTournament.registrationDeadline)}</span>
                  </div>
                )}
              </div>
            </div>

            {(currentTournament.description || currentTournament.prizePool) && (
              <div className="info-card full-width">
                <div className="info-header">
                  <h3>📝 Additional Information</h3>
                </div>
                <div className="info-content">
                  {currentTournament.description && (
                    <div className="info-row description-row">
                      <span className="info-label">Description</span>
                      <span className="info-value">{currentTournament.description}</span>
                    </div>
                  )}
                  {currentTournament.prizePool && (
                    <div className="info-row">
                      <span className="info-label">Prize Pool</span>
                      <span className="info-value">{currentTournament.prizePool}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

{/* Teams Section */}
{teams.length > 0 && (
  <div className="teams-section">
    <h2 className="section-title">
      <span className="title-icon">👥</span>
      Participating Teams
      <span className="count-badge">{teams.length}</span>
    </h2>
    <div className="teams-showcase">
      {teams.map((team, index) => (
        <div key={team.teamId || index} className="team-showcase-card">
          <div className="team-card-header">
            <AuthenticatedImage
              src={constructImageUrl(team.teamLogo, 'team')}
              alt={team.teamName}
              className="team-showcase-logo"
              fallbackSrc={dummyImage}
            />
            <div className="team-title-section">
              <h4 className="team-name">{team.teamName}</h4>
              {team.teamType && (
                <span className={`team-badge ${team.teamType.toLowerCase()}`}>
                  {team.teamType}
                </span>
              )}
            </div>
          </div>
          {team.description && (
            <p className="team-description">{team.description}</p>
          )}
          {team.website && (
            <a 
              href={team.website} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="team-link"
            >
              Visit Website →
            </a>
          )}
        </div>
      ))}
    </div>
  </div>
)}

        {/* Sponsors Section */}
        {sponsors.length > 0 && (
          <div className="sponsors-section">
            <h2 className="section-title">
              <span className="title-icon">🤝</span>
              Tournament Sponsors
              <span className="count-badge">{sponsors.length}</span>
            </h2>
            <div className="sponsors-showcase">
              {sponsors.map((sponsor, index) => (
                <div key={sponsor.sponsorId || index} className="sponsor-showcase-card">
                  <div className="sponsor-card-header">
                    <AuthenticatedImage
                      src={constructImageUrl(sponsor.sponsorLogo, 'sponsor')}
                      alt={sponsor.sponsorName}
                      className="sponsor-showcase-logo"
                      fallbackSrc={dummyImage}
                    />
                    <div className="sponsor-title-section">
                      <h4 className="sponsor-name">{sponsor.sponsorName}</h4>
                      {sponsor.sponsorshipType && (
                        <span className={`sponsorship-badge ${sponsor.sponsorshipType.toLowerCase()}`}>
                          {sponsor.sponsorshipType}
                        </span>
                      )}
                    </div>
                  </div>
                  {sponsor.description && (
                    <p className="sponsor-description">{sponsor.description}</p>
                  )}
                  {sponsor.website && (
                    <a 
                      href={sponsor.website} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="sponsor-link"
                    >
                      Visit Website →
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default TournamentDetails;